import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { configApi, type SiteConfig } from '../api/config'

export const useConfigStore = defineStore('config', () => {
  const siteConfig = ref<SiteConfig | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // 获取站点名称
  const siteName = computed(() => siteConfig.value?.name || '我爱纪录片')

  // 获取站点slogan
  const siteSlogan = computed(() => siteConfig.value?.slogan || '探索世界精彩纪录片')

  // 获取版权信息
  const copyright = computed(() => siteConfig.value?.copyright || '我爱纪录片')

  // 获取标题
  const pageTitle = computed(() => `${siteName.value} - ${siteSlogan.value}`)

  // 加载站点配置
  const loadSiteConfig = async () => {
    if (siteConfig.value) {
      return // 已加载过
    }

    loading.value = true
    error.value = null

    try {
      const response = await configApi.getSiteConfig()
      if (response.data) {
        siteConfig.value = response.data
        // 更新页面标题和元数据
        document.title = pageTitle.value

        // 更新作者信息
        const authorMeta = document.getElementById('page-author') as HTMLMetaElement
        if (authorMeta) {
          authorMeta.content = siteName.value
        }

        // 更新 Open Graph 标签
        const ogTitleMeta = document.querySelector('meta[property="og:title"]') as HTMLMetaElement
        if (ogTitleMeta) {
          ogTitleMeta.content = pageTitle.value
        }

        const ogSiteNameMeta = document.querySelector('meta[property="og:site_name"]') as HTMLMetaElement
        if (ogSiteNameMeta) {
          ogSiteNameMeta.content = siteName.value
        }

        // 更新 Twitter 标签
        const twitterTitleMeta = document.querySelector('meta[property="twitter:title"]') as HTMLMetaElement
        if (twitterTitleMeta) {
          twitterTitleMeta.content = pageTitle.value
        }

        // 更新 PWA 应用名称
        const appNameMeta = document.querySelector('meta[name="application-name"]') as HTMLMetaElement
        if (appNameMeta) {
          appNameMeta.content = siteName.value
        }

        const mobileWebAppTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement
        if (mobileWebAppTitleMeta) {
          mobileWebAppTitleMeta.content = siteName.value
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : '加载站点配置失败'
      console.error('Failed to load site config:', err)
    } finally {
      loading.value = false
    }
  }

  // 更新页面标题
  const updateTitle = (subtitle?: string) => {
    if (subtitle) {
      document.title = `${subtitle} - ${siteName.value}`
    } else {
      document.title = pageTitle.value
    }
  }

  return {
    siteConfig,
    loading,
    error,
    siteName,
    siteSlogan,
    copyright,
    pageTitle,
    loadSiteConfig,
    updateTitle
  }
})
